import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { ShieldCheckIcon, ArrowLeftIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import authService from '../services/auth';
import { UserProfile } from '../types';

const TwoFactorPage: React.FC = () => {
  const { twoFactorAuth } = useAuth();
  const navigate = useNavigate();
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [resendCountdown, setResendCountdown] = useState(0);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    // Récupère le profil utilisateur au chargement
    const fetchUserProfile = async () => {
      try {
        const profile = await authService.getUserProfile();
        console.log('Profil utilisateur récupéré:', profile);
        setUserProfile(profile);
      } catch (err) {
        console.error('Erreur lors de la récupération du profil:', err);
      }
    };

    fetchUserProfile();
  }, []);

  useEffect(() => {
    if (resendCountdown > 0) {
      const timer = setTimeout(() => setResendCountdown(resendCountdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCountdown]);

  const handleCodeChange = (index: number, value: string) => {
    if (value.length > 1) return; // Prevent multiple characters
    
    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const fullCode = code.join('');
    
    if (fullCode.length !== 6) {
      setError('Veuillez entrer le code complet à 6 chiffres');
      return;
    }

    setError('');
    setIsLoading(true);

    try {
      await twoFactorAuth({ code: fullCode });
      navigate('/dashboard');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Code invalide. Veuillez réessayer.');
      setCode(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = () => {
    setResendCountdown(60);
    // TODO: Implement resend code logic
  };

  return (
    <div className="min-h-screen gradient-bg flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          {/* Logo CCA */}
          <div className="text-center mb-8">
            <div className="w-20 h-20 mx-auto bg-blue-600 rounded-full flex items-center justify-center mb-4">
              <ShieldCheckIcon className="h-10 w-10 text-white" />
            </div>
            <h1 className="text-2xl font-poppins font-semibold text-dark-black">
              Authentification à deux facteurs
            </h1>
            <p className="text-gray-600 text-sm mt-2">
              Entrez le code de votre application d'authentification
            </p>
          </div>

          {/* User Info - Affichage des vraies informations */}
          <div className="bg-light-gray rounded-lg p-4 mb-6">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-bright-orange rounded-full flex items-center justify-center">
                <span className="text-white font-medium text-sm">
                  {userProfile?.first_name && userProfile?.last_name 
                    ? `${userProfile.first_name[0]}${userProfile.last_name[0]}` 
                    : 'U'
                  }
                </span>
              </div>
              <div>
                <p className="font-medium text-dark-black">
                  {userProfile?.first_name && userProfile?.last_name 
                    ? `${userProfile.first_name} ${userProfile.last_name}` 
                    : 'Chargement...'
                  }
                </p>
                <p className="text-sm text-gray-600">
                  {userProfile?.email || 'Chargement...'}
                </p>
                <p className="text-xs text-blue-600 mt-1">
                  🔐 Authentification à deux facteurs activée
                </p>
              </div>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {/* 2FA Code Input */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-dark-black mb-2">
                Code de vérification
              </label>
              <div className="flex space-x-2 justify-center">
                {code.map((digit, index) => (
                  <input
                    key={index}
                    ref={(el) => {
                      inputRefs.current[index] = el;
                    }}
                    type="text"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleCodeChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    className="w-12 h-12 text-center text-xl font-bold border border-gray-300 rounded-lg focus:ring-2 focus:ring-bright-orange focus:border-transparent transition-all"
                    placeholder="0"
                  />
                ))}
              </div>
              <p className="text-xs text-gray-500 text-center mt-2">
                Entrez le code à 6 chiffres de votre application
              </p>
            </div>

            <button
              type="submit"
              disabled={isLoading || code.join('').length !== 6}
              className="w-full btn-primary text-white py-3 rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Vérification...
                </div>
              ) : (
                <>
                  <ShieldCheckIcon className="inline-block h-5 w-5 mr-2" />
                  Vérifier le code
                </>
              )}
            </button>

            <div className="text-center">
              <p className="text-sm text-gray-600 mb-3">Vous n'avez pas reçu le code ?</p>
              <button
                type="button"
                onClick={handleResendCode}
                disabled={resendCountdown > 0}
                className="text-sm text-bright-orange hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ArrowPathIcon className="inline-block h-4 w-4 mr-1" />
                {resendCountdown > 0 
                  ? `Renvoyer dans ${resendCountdown}s` 
                  : 'Renvoyer un nouveau code'
                }
              </button>
            </div>
          </form>

          {/* Backup Options */}
          <div className="mt-6 p-4 bg-blue-50 rounded-lg">
            <h3 className="font-medium text-dark-black mb-2">Options de récupération</h3>
            <div className="space-y-2">
              <button className="w-full text-left text-sm text-blue-600 hover:underline">
                Recevoir le code par SMS
              </button>
              <button className="w-full text-left text-sm text-blue-600 hover:underline">
                Utiliser un code de sauvegarde
              </button>
            </div>
          </div>

          <div className="mt-6 text-center">
            <Link
              to="/login"
              className="text-sm text-gray-600 hover:text-bright-orange transition-colors inline-flex items-center"
            >
              <ArrowLeftIcon className="h-4 w-4 mr-1" />
              Retour à la connexion
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TwoFactorPage;
